import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useLicense } from '../../context/LicenseContext';
import { useNavigate } from 'react-router-dom';

const API = '/api';
const getToken = () => localStorage.getItem('lemai_token') || '';
const authHdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrgRow {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  enabled_modules: number;
  user_count: number;
  created_at: string;
  tier_name: string;
  max_users: number;
  expires_at: string | null;
}

interface LicenseMeta {
  license_key: string;
  tier_name: string;
  max_users: number;
  expires_at: string | null;
  notes: string;
  user_count: number;
  org_name: string;
  flags: Record<string, boolean>;
}

interface ModuleRow {
  slug: string;
  name: string;
  category: string;
  price_monthly: number;
  is_enabled: boolean;
  camera_licences: number;
}

interface OrgUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  custom_permissions: Record<string, Record<string, boolean>>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TIER_PRESETS = [
  { label: 'Free',         max_users: 5,   color: '#555555' },
  { label: 'Starter',      max_users: 10,  color: '#22c55e' },
  { label: 'Professional', max_users: 25,  color: '#3b82f6' },
  { label: 'Enterprise',   max_users: 999, color: '#a855f7' },
  { label: 'Custom',       max_users: 0,   color: '#f59e0b' },
];

const ROLES = [
  { value: 'viewer',     label: 'Viewer',     desc: 'Read-only access to licensed modules',          color: '#555555' },
  { value: 'operator',   label: 'Operator',   desc: 'Can interact — acknowledge alarms, update jobs', color: '#3b82f6' },
  { value: 'supervisor', label: 'Supervisor', desc: 'Can manage incidents, run reports',              color: '#22c55e' },
  { value: 'manager',    label: 'Manager',    desc: 'Full module access, analytics, exports',         color: '#f59e0b' },
  { value: 'admin',      label: 'Admin',      desc: 'Manages users within this organisation',         color: '#a855f7' },
];

const CAT_COLOR: Record<string, string> = {
  security: '#3b82f6', operations: '#22c55e', analytics: '#a855f7', system: '#555555', safety: '#f59e0b',
};

// Per-module feature definitions — what each module contains
const MODULE_FEATURES: Record<string, { label: string; features: string[] }> = {
  soc:       { label: 'SOC Dashboard',      features: ['View Alarms', 'Acknowledge Alarm', 'Close Alarm', 'Access SOP', 'Create Job', 'CCTV Review', 'Suppress Alarm'] },
  video:     { label: 'Video',              features: ['Live View', 'Playback', 'Export Clip', 'PTZ Control', 'Camera Management'] },
  vision:    { label: 'Smart Vision',       features: ['Live Events', 'Heatmap Access', 'People Counting', 'Vehicle Detection', 'Face Detection', 'Loitering Alerts', 'Export Data'] },
  access:    { label: 'Access Control',     features: ['View Door Status', 'Lock / Unlock Doors', 'View Access Logs', 'Emergency Lockdown', 'Manage Cardholders'] },
  incidents: { label: 'Incidents',          features: ['View Incidents', 'Create Incident', 'Edit Incident', 'Close Incident', 'Export Reports'] },
  jobs:      { label: 'Jobs',               features: ['View Jobs', 'Create Job', 'Assign Job', 'Close Job', 'Export'] },
  reports:   { label: 'Reports',            features: ['View Reports', 'Generate Reports', 'Export PDF', 'Schedule Reports'] },
  executive: { label: 'Executive Dashboard',features: ['View Dashboard', 'KPI Metrics', 'Trend Analysis', 'Export Data'] },
  assets:    { label: 'Assets',             features: ['View Assets', 'Add Asset', 'Edit Asset', 'Dispose Asset', 'Maintenance Logs'] },
  carpark:   { label: 'Car Park',           features: ['View Occupancy', 'Manual Override', 'LPR Events', 'Overstay Alerts', 'Export Reports'] },
  handover:  { label: 'Shift Handover',     features: ['View Handovers', 'Create Handover', 'Sign Off Handover'] },
  sitemap:   { label: 'Site Map',           features: ['View Map', 'View Alarm Locations', 'View Camera Locations'] },
};

// ─── License status helper ────────────────────────────────────────────────────

function licenseStatus(expires_at: string | null): { label: string; color: string; bg: string } {
  if (!expires_at) return { label: 'No Expiry', color: '#22c55e', bg: 'rgba(34,197,94,0.1)' };
  const diff = new Date(expires_at).getTime() - Date.now();
  if (diff < 0)                   return { label: 'Expired',   color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
  if (diff < 30 * 24 * 3600_000) return { label: 'Expiring',  color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' };
  return { label: 'Active', color: '#22c55e', bg: 'rgba(34,197,94,0.1)' };
}

function tierColor(tier: string) {
  return TIER_PRESETS.find(t => t.label === tier)?.color || '#555555';
}

// ─── Seed audit log ───────────────────────────────────────────────────────────

const SEED_AUDIT = [
  { id:'A001', ts:'2026-04-20 09:41', user:'council@lemai.ai',    action:'MODULE_ACCESS',      module:'Smart Vision',    detail:'Viewed heatmap analytics — Civic Square',                   sev:'info' },
  { id:'A002', ts:'2026-04-20 09:38', user:'operator1@lemai.ai', action:'ALARM_ACKNOWLEDGED', module:'SOC Dashboard',   detail:'Acknowledged alarm — Duress Button — Town Hall',             sev:'warning' },
  { id:'A003', ts:'2026-04-20 09:22', user:'council@lemai.ai',    action:'EXPORT_REPORT',      module:'Reports',         detail:'Exported PDF — Monthly Incident Summary — March 2026',       sev:'info' },
  { id:'A004', ts:'2026-04-20 08:55', user:'admin@lemai.ai',     action:'PERMISSION_CHANGE',  module:'Org Management',  detail:'Updated permissions for operator1@lemai.ai — removed PTZ Control access', sev:'warning' },
  { id:'A005', ts:'2026-04-20 08:44', user:'operator1@lemai.ai', action:'DOOR_UNLOCKED',      module:'Access Control',  detail:'Unlocked — Main Entrance — Council Chambers',                sev:'warning' },
  { id:'A006', ts:'2026-04-20 08:31', user:'council@lemai.ai',    action:'INCIDENT_CREATED',   module:'Incidents',       detail:'Created incident #2247 — Suspicious Person — Civic Square',  sev:'info' },
  { id:'A007', ts:'2026-04-20 08:22', user:'operator1@lemai.ai', action:'LOGIN',              module:'Auth',            detail:'Successful login from 192.168.1.42',                          sev:'info' },
  { id:'A008', ts:'2026-04-19 17:44', user:'council@lemai.ai',    action:'ALARM_CLOSED',       module:'SOC Dashboard',   detail:'Closed alarm — Camera Tamper — Aquatic Centre',              sev:'info' },
];

// ─── Mini Toggle ──────────────────────────────────────────────────────────────

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div onClick={onToggle} style={{ width:38, height:22, borderRadius:11, background: on ? '#3b82f6' : '#111111', border:`1px solid ${on ? '#3b82f6' : '#222222'}`, cursor:'pointer', position:'relative', flexShrink:0, transition:'background 0.2s' }}>
      <div style={{ position:'absolute', top:3, left: on ? 18 : 3, width:14, height:14, borderRadius:'50%', background:'white', transition:'left 0.2s' }} />
    </div>
  );
}

// ─── Role badge ───────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  const r = ROLES.find(x => x.value === role) || ROLES[1];
  return (
    <span style={{ fontSize:11, background:`${r.color}18`, border:`1px solid ${r.color}40`, borderRadius:6, padding:'2px 8px', color:r.color, fontWeight:600, textTransform:'capitalize' }}>
      {r.label}
    </span>
  );
}

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyBtn({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{ background: copied ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.07)', border:`1px solid ${copied ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.12)'}`, borderRadius:6, color: copied ? '#4ade80' : '#94a3b8', fontSize:11, padding:'3px 10px', cursor:'pointer', fontWeight:600, transition:'all 0.2s' }}>
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function OrgManagement() {
  const { user } = useAuth();
  const { isSuperAdmin, switchOrg } = useLicense();
  const navigate = useNavigate();

  const [orgs, setOrgs]               = useState<OrgRow[]>([]);
  const [loading, setLoading]         = useState(true);
  const [selectedOrg, setSelectedOrg] = useState<OrgRow | null>(null);
  const [tab, setTab]                 = useState<'license' | 'modules' | 'users' | 'permissions' | 'audit'>('license');

  // License
  const [licenseMeta, setLicenseMeta] = useState<LicenseMeta | null>(null);
  const [licEdit, setLicEdit]         = useState<Partial<LicenseMeta>>({});
  const [licSaving, setLicSaving]     = useState(false);

  // Modules
  const [modules, setModules]         = useState<ModuleRow[]>([]);
  const [modSaving, setModSaving]     = useState(false);

  // Users
  const [orgUsers, setOrgUsers]       = useState<OrgUser[]>([]);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser]         = useState({ email:'', password:'', first_name:'', last_name:'', role:'operator' });

  // Permissions
  const [selectedUser, setSelectedUser]   = useState<OrgUser | null>(null);
  const [permState, setPermState]         = useState<Record<string, Record<string, boolean>>>({});
  const [permSaving, setPermSaving]       = useState(false);

  // New org
  const [showNewOrg, setShowNewOrg]   = useState(false);
  const [newOrgName, setNewOrgName]   = useState('');

  const [message, setMessage]         = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) { navigate('/dashboard'); return; }
    loadOrgs();
  }, [isSuperAdmin]);

  function showMsg(text: string, ok = true) {
    setMessage({ text, ok });
    setTimeout(() => setMessage(null), 3500);
  }

  async function loadOrgs() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/orgs`, { headers: authHdr() });
      const d = await r.json();
      setOrgs(d.orgs || []);
    } finally { setLoading(false); }
  }

  async function selectOrg(org: OrgRow) {
    setSelectedOrg(org);
    setTab('license');
    setSelectedUser(null);
    const [lr, mr, ur] = await Promise.all([
      fetch(`${API}/admin/orgs/${org.id}/license`,  { headers: authHdr() }),
      fetch(`${API}/admin/orgs/${org.id}/modules`,  { headers: authHdr() }),
      fetch(`${API}/admin/orgs/${org.id}/users`,    { headers: authHdr() }),
    ]);
    const ld = await lr.json();
    const md = await mr.json();
    const ud = await ur.json();
    setLicenseMeta(ld);
    setLicEdit({ tier_name: ld.tier_name, max_users: ld.max_users, expires_at: ld.expires_at, notes: ld.notes, flags: ld.flags || {} });
    setModules(md.modules || []);
    setOrgUsers(ud.users || []);
    // Pre-fill permState from user custom_permissions
    const ps: Record<string, Record<string, boolean>> = {};
    for (const u of (ud.users || [])) {
      if (u.custom_permissions) {
        for (const [slug, feats] of Object.entries(u.custom_permissions as Record<string, Record<string, boolean>>)) {
          ps[`${u.id}-${slug}`] = feats;
        }
      }
    }
    setPermState(ps);
  }

  // ── License ──────────────────────────────────────────────────────────────────

  async function saveLicense() {
    if (!selectedOrg) return;
    setLicSaving(true);
    try {
      const r = await fetch(`${API}/admin/orgs/${selectedOrg.id}/license`, {
        method: 'PUT', headers: authHdr(), body: JSON.stringify(licEdit),
      });
      const d = await r.json();
      setLicenseMeta(d);
      setLicEdit({ tier_name: d.tier_name, max_users: d.max_users, expires_at: d.expires_at, notes: d.notes });
      showMsg('License updated');
      loadOrgs();
    } finally { setLicSaving(false); }
  }

  // ── Modules ──────────────────────────────────────────────────────────────────

  function toggleModule(slug: string) {
    setModules(prev => prev.map(m => m.slug === slug ? { ...m, is_enabled: !m.is_enabled } : m));
  }

  function setCameraLicences(slug: string, val: number) {
    setModules(prev => prev.map(m => m.slug === slug ? { ...m, camera_licences: val } : m));
  }

  async function saveModules() {
    if (!selectedOrg) return;
    setModSaving(true);
    try {
      await fetch(`${API}/admin/orgs/${selectedOrg.id}/modules`, {
        method: 'PUT', headers: authHdr(), body: JSON.stringify({ modules }),
      });
      showMsg('Module access updated');
      loadOrgs();
    } finally { setModSaving(false); }
  }

  // ── Users ─────────────────────────────────────────────────────────────────────

  async function reloadUsers() {
    if (!selectedOrg) return;
    const ud = await (await fetch(`${API}/admin/orgs/${selectedOrg.id}/users`, { headers: authHdr() })).json();
    setOrgUsers(ud.users || []);
  }

  async function createUser() {
    if (!selectedOrg || !newUser.email || !newUser.password || !newUser.first_name) return;
    const r = await fetch(`${API}/admin/orgs/${selectedOrg.id}/users`, {
      method: 'POST', headers: authHdr(), body: JSON.stringify(newUser),
    });
    if (r.ok) {
      setNewUser({ email:'', password:'', first_name:'', last_name:'', role:'operator' });
      setShowNewUser(false);
      await reloadUsers();
      showMsg('User created');
    } else {
      const d = await r.json();
      showMsg(d.error || 'Error creating user', false);
    }
  }

  async function updateUserRole(userId: number, role: string) {
    await fetch(`${API}/admin/users/${userId}/role`, {
      method: 'PUT', headers: authHdr(), body: JSON.stringify({ role }),
    });
    setOrgUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    showMsg('Role updated');
  }

  async function toggleUserActive(userId: number, is_active: boolean) {
    await fetch(`${API}/admin/users/${userId}/status`, {
      method: 'PUT', headers: authHdr(), body: JSON.stringify({ is_active }),
    });
    setOrgUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active } : u));
    showMsg(is_active ? 'User activated' : 'User deactivated');
  }

  // ── Permissions ──────────────────────────────────────────────────────────────

  function getPerms(userId: number, slug: string) {
    return permState[`${userId}-${slug}`] || {};
  }

  function setFeature(userId: number, slug: string, feature: string, val: boolean) {
    const key = `${userId}-${slug}`;
    setPermState(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [feature]: val } }));
  }

  function setAllFeatures(userId: number, slug: string, features: string[], val: boolean) {
    const key = `${userId}-${slug}`;
    const obj: Record<string, boolean> = {};
    features.forEach(f => { obj[f] = val; });
    setPermState(prev => ({ ...prev, [key]: obj }));
  }

  async function savePermissions() {
    if (!selectedUser) return;
    setPermSaving(true);
    // Build { slug: { feature: bool } } from permState for this user
    const permissions: Record<string, Record<string, boolean>> = {};
    for (const [key, feats] of Object.entries(permState)) {
      const [uid, ...slugParts] = key.split('-');
      if (Number(uid) === selectedUser.id) {
        permissions[slugParts.join('-')] = feats;
      }
    }
    try {
      await fetch(`${API}/admin/users/${selectedUser.id}/permissions`, {
        method: 'PUT', headers: authHdr(), body: JSON.stringify({ permissions }),
      });
      showMsg(`Permissions saved for ${selectedUser.first_name}`);
    } finally { setPermSaving(false); }
  }

  // ── New Org ───────────────────────────────────────────────────────────────────

  async function createOrg() {
    if (!newOrgName.trim()) return;
    const r = await fetch(`${API}/admin/orgs`, {
      method: 'POST', headers: authHdr(), body: JSON.stringify({ name: newOrgName }),
    });
    if (r.ok) {
      setNewOrgName(''); setShowNewOrg(false);
      await loadOrgs();
      showMsg('Organisation created');
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const grouped = modules.reduce<Record<string, ModuleRow[]>>((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});

  const enabledCount = modules.filter(m => m.is_enabled).length;
  const userCount = licenseMeta?.user_count ?? orgUsers.length;
  const maxUsers = licEdit.max_users ?? 5;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Layout title="Licence & Access Management" subtitle="Manage client organisations, module licences, and user permissions">

      {message && (
        <div style={{ background: message.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', border:`1px solid ${message.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius:10, padding:'10px 16px', marginBottom:16, color: message.ok ? '#4ade80' : '#fca5a5', fontSize:13 }}>
          {message.ok ? '✓' : '✕'} {message.text}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap:20, minHeight:640 }}>

        {/* ── Left: Org List ──────────────────────────────────────────────── */}
        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:14, overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'14px 16px', borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'white' }}>Clients ({orgs.length})</div>
            <button onClick={() => setShowNewOrg(true)} style={{ background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:6, color:'#93c5fd', fontSize:12, padding:'4px 10px', cursor:'pointer', fontWeight:600 }}>+ New</button>
          </div>

          {showNewOrg && (
            <div style={{ padding:12, borderBottom:'1px solid #1a1a1a', background:'rgba(59,130,246,0.05)' }}>
              <input value={newOrgName} onChange={e => setNewOrgName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createOrg()} placeholder="Organisation name" autoFocus
                style={{ width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6, padding:'7px 10px', color:'white', fontSize:12, outline:'none', boxSizing:'border-box', marginBottom:8 }} />
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={createOrg} style={{ flex:1, background:'#3b82f6', border:'none', borderRadius:6, color:'white', padding:6, fontSize:12, cursor:'pointer', fontWeight:600 }}>Create</button>
                <button onClick={() => setShowNewOrg(false)} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:6, color:'#555555', padding:'6px 10px', fontSize:12, cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ flex:1, overflowY:'auto' }}>
            {loading ? Array.from({length:4}).map((_,i) => (
              <div key={i} style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <div className="skeleton" style={{ height:13, width:140, marginBottom:6 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <div className="skeleton" style={{ height:10, width:60 }} />
                  <div className="skeleton" style={{ height:10, width:50 }} />
                </div>
              </div>
            )) : orgs.map(org => {
              const st = licenseStatus(org.expires_at);
              const tc = tierColor(org.tier_name);
              const isSelected = selectedOrg?.id === org.id;
              return (
                <div key={org.id} onClick={() => selectOrg(org)}
                  style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', background: isSelected ? 'rgba(59,130,246,0.1)' : 'transparent', borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent', transition:'background 0.15s' }}
                  onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background='rgba(255,255,255,0.02)'; }}
                  onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background='transparent'; }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                    <div style={{ fontSize:13, fontWeight:600, color: isSelected ? '#93c5fd' : 'white', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{org.name}</div>
                    <span style={{ fontSize:10, background:`${tc}18`, border:`1px solid ${tc}40`, borderRadius:5, padding:'1px 6px', color:tc, fontWeight:700, flexShrink:0 }}>{org.tier_name}</span>
                  </div>
                  <div style={{ display:'flex', gap:8, fontSize:11, color:'#555555', flexWrap:'wrap' }}>
                    <span>{org.enabled_modules} modules</span>
                    <span>·</span>
                    <span>{org.user_count}/{org.max_users === 999 ? '∞' : org.max_users} users</span>
                    <span>·</span>
                    <span style={{ color: st.color }}>{st.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right: Detail Panel ─────────────────────────────────────────── */}
        <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:14, overflow:'hidden', display:'flex', flexDirection:'column' }}>
          {!selectedOrg ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#444444', gap:12 }}>
              <div style={{ fontSize:40 }}>🏢</div>
              <div style={{ fontSize:14, fontWeight:600, color:'#555555' }}>Select a client to manage</div>
              <div style={{ fontSize:12, color:'#444444' }}>Or create a new organisation using the + New button</div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding:'16px 20px', borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:`${tierColor(selectedOrg.tier_name)}20`, border:`1px solid ${tierColor(selectedOrg.tier_name)}40`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>🏢</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:17, fontWeight:700, color:'white' }}>{selectedOrg.name}</div>
                  <div style={{ fontSize:12, color:'#555555', marginTop:2 }}>
                    {enabledCount} module{enabledCount !== 1 ? 's' : ''} active · {userCount} of {maxUsers === 999 ? '∞' : maxUsers} users
                  </div>
                </div>
                <button onClick={() => switchOrg(selectedOrg.id)} style={{ background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:8, color:'#f59e0b', fontSize:12, padding:'6px 12px', cursor:'pointer', fontWeight:600 }}>
                  👁 Preview as Client
                </button>
              </div>

              {/* Tabs */}
              <div style={{ display:'flex', borderBottom:'1px solid #1a1a1a', padding:'0 4px', gap:2, flexWrap:'wrap' }}>
                {([
                  ['license',     '🔑 Licence'],
                  ['modules',     `📦 Modules (${enabledCount})`],
                  ['users',       `👤 Users (${orgUsers.length})`],
                  ['permissions', '🛡 Permissions'],
                  ['audit',       '📋 Audit Log'],
                ] as [typeof tab, string][]).map(([t, label]) => (
                  <button key={t} onClick={() => setTab(t)} style={{ padding:'10px 14px', background:'none', border:'none', borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent', color: tab === t ? '#93c5fd' : '#555555', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div style={{ flex:1, overflowY:'auto', padding:'20px' }}>

                {/* ── LICENCE TAB ─────────────────────────────────────────── */}
                {tab === 'license' && licenseMeta && (
                  <div>
                    {/* License Key */}
                    <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#3b82f6', letterSpacing:'1px', marginBottom:8 }}>LICENCE KEY</div>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ fontFamily:'monospace', fontSize:16, fontWeight:700, color:'white', letterSpacing:'2px', flex:1 }}>{licenseMeta.license_key}</div>
                        <CopyBtn value={licenseMeta.license_key} />
                      </div>
                      <div style={{ fontSize:11, color:'#555555', marginTop:6 }}>Share this key with the client — it identifies their organisation in the Lemai platform.</div>
                    </div>

                    {/* Status row */}
                    {(() => {
                      const st = licenseStatus(licEdit.expires_at ?? null);
                      const tc = tierColor(licEdit.tier_name || 'Free');
                      return (
                        <div style={{ display:'flex', gap:10, marginBottom:20 }}>
                          <div style={{ flex:1, background: st.bg, border:`1px solid ${st.color}40`, borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                            <div style={{ fontSize:11, color:st.color, fontWeight:700, letterSpacing:'0.5px' }}>STATUS</div>
                            <div style={{ fontSize:18, fontWeight:700, color:st.color, marginTop:4 }}>{st.label}</div>
                          </div>
                          <div style={{ flex:1, background:`${tc}10`, border:`1px solid ${tc}30`, borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                            <div style={{ fontSize:11, color:tc, fontWeight:700, letterSpacing:'0.5px' }}>TIER</div>
                            <div style={{ fontSize:18, fontWeight:700, color:tc, marginTop:4 }}>{licEdit.tier_name || 'Free'}</div>
                          </div>
                          <div style={{ flex:1, background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                            <div style={{ fontSize:11, color:'#555555', fontWeight:700, letterSpacing:'0.5px' }}>USERS</div>
                            <div style={{ fontSize:18, fontWeight:700, color:'white', marginTop:4 }}>
                              {userCount} <span style={{ fontSize:13, color:'#555555' }}>/ {maxUsers === 999 ? '∞' : maxUsers}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Edit fields */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                      {/* Tier */}
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#555555', marginBottom:6, letterSpacing:'0.5px', textTransform:'uppercase' }}>Tier / Package Name</label>
                        <input value={licEdit.tier_name || ''} onChange={e => setLicEdit(p => ({ ...p, tier_name: e.target.value }))}
                          placeholder="e.g. Council Pro"
                          style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'9px 12px', color:'white', fontSize:13, outline:'none', boxSizing:'border-box' }} />
                        {/* Preset chips */}
                        <div style={{ display:'flex', gap:5, marginTop:7, flexWrap:'wrap' }}>
                          {TIER_PRESETS.filter(t => t.label !== 'Custom').map(t => (
                            <button key={t.label} onClick={() => setLicEdit(p => ({ ...p, tier_name: t.label, max_users: t.max_users }))}
                              style={{ fontSize:10, padding:'2px 8px', borderRadius:6, border:`1px solid ${t.color}40`, background:`${t.color}12`, color:t.color, cursor:'pointer', fontWeight:600 }}>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Max Users */}
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#555555', marginBottom:6, letterSpacing:'0.5px', textTransform:'uppercase' }}>User Limit</label>
                        <input type="number" min={1} value={licEdit.max_users ?? 5} onChange={e => setLicEdit(p => ({ ...p, max_users: Number(e.target.value) }))}
                          style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'9px 12px', color:'white', fontSize:13, outline:'none', boxSizing:'border-box' }} />
                        <div style={{ display:'flex', gap:5, marginTop:7 }}>
                          {[5,10,25,50,999].map(n => (
                            <button key={n} onClick={() => setLicEdit(p => ({ ...p, max_users: n }))}
                              style={{ fontSize:10, padding:'2px 8px', borderRadius:6, border:'1px solid rgba(255,255,255,0.1)', background: licEdit.max_users === n ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.04)', color: licEdit.max_users === n ? '#93c5fd' : '#555555', cursor:'pointer', fontWeight:600 }}>
                              {n === 999 ? '∞' : n}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Expiry */}
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#555555', marginBottom:6, letterSpacing:'0.5px', textTransform:'uppercase' }}>Expiry Date</label>
                        <input type="date" value={licEdit.expires_at ? licEdit.expires_at.split('T')[0] : ''}
                          onChange={e => setLicEdit(p => ({ ...p, expires_at: e.target.value ? e.target.value + 'T00:00:00Z' : null }))}
                          style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'9px 12px', color:'white', fontSize:13, outline:'none', boxSizing:'border-box', colorScheme:'dark' }} />
                        {licEdit.expires_at && (
                          <button onClick={() => setLicEdit(p => ({ ...p, expires_at: null }))} style={{ marginTop:5, fontSize:10, color:'#555555', background:'none', border:'none', cursor:'pointer', padding:0 }}>
                            ✕ Remove expiry (no expiry = permanent)
                          </button>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#555555', marginBottom:6, letterSpacing:'0.5px', textTransform:'uppercase' }}>Internal Notes</label>
                        <textarea value={licEdit.notes || ''} onChange={e => setLicEdit(p => ({ ...p, notes: e.target.value }))}
                          placeholder="e.g. Council contract ref #2026-42, renewal due Dec 2026"
                          style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'9px 12px', color:'white', fontSize:13, outline:'none', boxSizing:'border-box', resize:'none', height:64, fontFamily:'inherit' }} />
                      </div>
                    </div>

                    {/* Platform Configuration flags */}
                    <div style={{ gridColumn:'span 2', background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:'14px 16px', marginBottom:4 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#555555', letterSpacing:'1px', textTransform:'uppercase', marginBottom:12 }}>Platform Configuration</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {([
                          { key:'is_demo',          label:'Demo Mode',             desc:'Show seed/demo data instead of live API data. Use for trials and presentations.' },
                          { key:'protege_enabled',  label:'Protege GX Integration',desc:'Enable live Inner Range Protege GX access control data for this organisation.' },
                          { key:'armorip_enabled',  label:'ArmorIP Integration',   desc:'Enable live ArmorIP alarm receiver integration.' },
                        ] as { key: string; label: string; desc: string }[]).map(({ key, label, desc }) => {
                          const on = !!(licEdit as any).flags?.[key];
                          return (
                            <div key={key} style={{ display:'flex', alignItems:'center', gap:12 }}>
                              <Toggle on={on} onToggle={() => setLicEdit(p => ({ ...p, flags: { ...(p as any).flags, [key]: !on } }))} />
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:600, color: on ? '#e2e8f0' : '#555555' }}>{label}</div>
                                <div style={{ fontSize:11, color:'#444444', marginTop:1 }}>{desc}</div>
                              </div>
                              {on && <span style={{ fontSize:10, fontWeight:700, background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:5, padding:'2px 8px', color:'#4ade80' }}>ON</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <button onClick={saveLicense} disabled={licSaving} style={{ width:'100%', padding:12, background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:10, color:'white', fontSize:14, fontWeight:700, cursor: licSaving ? 'wait' : 'pointer', opacity: licSaving ? 0.7 : 1 }}>
                      {licSaving ? 'Saving…' : 'Save Licence Settings'}
                    </button>
                  </div>
                )}

                {/* ── MODULES TAB ─────────────────────────────────────────── */}
                {tab === 'modules' && (
                  <>
                    <div style={{ fontSize:12, color:'#555555', marginBottom:16 }}>
                      Toggle which modules this client can access. Disabled modules show as locked in their navigation.
                    </div>
                    {Object.entries(grouped).map(([cat, mods]) => (
                      <div key={cat} style={{ marginBottom:20 }}>
                        <div style={{ fontSize:11, fontWeight:700, color: CAT_COLOR[cat] || '#555555', letterSpacing:'1px', textTransform:'uppercase', marginBottom:8 }}>{cat}</div>
                        {mods.map(m => (
                          <div key={m.slug} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', background:'#0d0d0d', border:'1px solid rgba(255,255,255,0.05)', borderRadius:10, marginBottom:6, opacity: m.is_enabled ? 1 : 0.6, transition:'opacity 0.2s' }}>
                            <Toggle on={m.is_enabled} onToggle={() => toggleModule(m.slug)} />
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:13, fontWeight:600, color: m.is_enabled ? 'white' : '#555555' }}>{m.name}</div>
                              <div style={{ fontSize:11, color:'#444444', marginTop:1 }}>/{m.slug} · ${m.price_monthly}/mo</div>
                            </div>
                            {m.slug === 'video' && m.is_enabled && (
                              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <span style={{ fontSize:11, color:'#555555' }}>Camera licences:</span>
                                <select value={m.camera_licences} onChange={e => setCameraLicences(m.slug, Number(e.target.value))}
                                  style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6, color:'white', fontSize:12, padding:'3px 6px' }}>
                                  {[0,5,10,25,50,100,200].map(n => <option key={n} value={n}>{n === 0 ? 'Unlimited' : `${n} cams`}</option>)}
                                </select>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                    <button onClick={saveModules} disabled={modSaving} style={{ width:'100%', padding:12, background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:10, color:'white', fontSize:14, fontWeight:700, cursor: modSaving ? 'wait' : 'pointer', opacity: modSaving ? 0.7 : 1 }}>
                      {modSaving ? 'Saving…' : 'Save Module Access'}
                    </button>
                  </>
                )}

                {/* ── USERS TAB ───────────────────────────────────────────── */}
                {tab === 'users' && (
                  <>
                    {/* User count vs limit warning */}
                    {userCount >= maxUsers && maxUsers !== 999 && (
                      <div style={{ background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:8, padding:'8px 14px', marginBottom:14, fontSize:12, color:'#f59e0b' }}>
                        ⚠ User limit reached ({userCount}/{maxUsers}). Upgrade the licence to add more users.
                      </div>
                    )}

                    <button onClick={() => setShowNewUser(true)} disabled={userCount >= maxUsers && maxUsers !== 999}
                      style={{ background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:8, color:'#93c5fd', fontSize:13, padding:'8px 16px', cursor:'pointer', fontWeight:600, marginBottom:16, opacity: (userCount >= maxUsers && maxUsers !== 999) ? 0.5 : 1 }}>
                      + Add User
                    </button>

                    {showNewUser && (
                      <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:12, padding:16, marginBottom:16 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'white', marginBottom:12 }}>New User</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                          {([['first_name','First Name'],['last_name','Last Name'],['email','Email'],['password','Password']] as const).map(([f, ph]) => (
                            <input key={f} value={newUser[f as keyof typeof newUser]} onChange={e => setNewUser(p => ({ ...p, [f]: e.target.value }))}
                              placeholder={ph} type={f === 'password' ? 'password' : 'text'}
                              style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'8px 12px', color:'white', fontSize:13, outline:'none' }} />
                          ))}
                        </div>
                        <div style={{ marginBottom:10 }}>
                          <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#555555', marginBottom:6 }}>ROLE</label>
                          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                            {ROLES.map(r => (
                              <button key={r.value} onClick={() => setNewUser(p => ({ ...p, role: r.value }))}
                                style={{ padding:'6px 12px', borderRadius:7, border:`1px solid ${newUser.role===r.value ? r.color : 'rgba(255,255,255,0.08)'}`, background: newUser.role===r.value ? `${r.color}18` : 'rgba(255,255,255,0.03)', color: newUser.role===r.value ? r.color : '#555555', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                                {r.label}
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize:11, color:'#555555', marginTop:6 }}>{ROLES.find(r => r.value === newUser.role)?.desc}</div>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          <button onClick={createUser} style={{ flex:1, background:'#3b82f6', border:'none', borderRadius:8, color:'white', padding:8, fontSize:13, cursor:'pointer', fontWeight:600 }}>Create User</button>
                          <button onClick={() => setShowNewUser(false)} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:8, color:'#555555', padding:'8px 14px', fontSize:13, cursor:'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {orgUsers.map(u => (
                      <div key={u.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'#0d0d0d', border:'1px solid rgba(255,255,255,0.05)', borderRadius:10, marginBottom:8, opacity: u.is_active ? 1 : 0.5 }}>
                        <div style={{ width:38, height:38, borderRadius:'50%', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'#93c5fd', flexShrink:0 }}>
                          {(u.first_name[0] || '?')}{(u.last_name[0] || '')}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:'white', display:'flex', alignItems:'center', gap:8 }}>
                            {u.first_name} {u.last_name}
                            {!u.is_active && <span style={{ fontSize:10, background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:4, padding:'1px 6px', color:'#fca5a5' }}>Inactive</span>}
                          </div>
                          <div style={{ fontSize:11, color:'#555555', marginTop:2 }}>{u.email}</div>
                        </div>
                        {/* Role selector */}
                        <select value={u.role} onChange={e => updateUserRole(u.id, e.target.value)}
                          style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:7, color:'white', fontSize:12, padding:'5px 8px', cursor:'pointer' }}>
                          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        {/* Active toggle */}
                        <button onClick={() => toggleUserActive(u.id, !u.is_active)}
                          style={{ background: u.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', border:`1px solid ${u.is_active ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`, borderRadius:7, color: u.is_active ? '#fca5a5' : '#4ade80', fontSize:11, padding:'5px 10px', cursor:'pointer', fontWeight:600, flexShrink:0 }}>
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    ))}
                    {orgUsers.length === 0 && <div style={{ textAlign:'center', color:'#444444', fontSize:13, padding:32 }}>No users yet. Click + Add User to get started.</div>}
                  </>
                )}

                {/* ── PERMISSIONS TAB ─────────────────────────────────────── */}
                {tab === 'permissions' && (
                  <div>
                    <div style={{ fontSize:12, color:'#555555', marginBottom:16 }}>
                      Fine-grained control over what each user can do within each module. Start by selecting a user below.
                    </div>

                    {/* User selector */}
                    <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
                      {orgUsers.map(u => (
                        <button key={u.id} onClick={() => setSelectedUser(selectedUser?.id === u.id ? null : u)}
                          style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:8, border:`1px solid ${selectedUser?.id===u.id ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.08)'}`, background: selectedUser?.id===u.id ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.03)', color: selectedUser?.id===u.id ? '#93c5fd' : '#94a3b8', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                          <span style={{ width:28, height:28, borderRadius:'50%', background:'rgba(59,130,246,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700 }}>
                            {(u.first_name[0] || '?')}{(u.last_name[0] || '')}
                          </span>
                          {u.first_name} {u.last_name}
                          <RoleBadge role={u.role} />
                        </button>
                      ))}
                      {orgUsers.length === 0 && <div style={{ color:'#444444', fontSize:13 }}>No users yet — add users in the Users tab first.</div>}
                    </div>

                    {selectedUser && (
                      <div>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                          <div>
                            <div style={{ fontSize:14, fontWeight:700, color:'#e5e5e5' }}>
                              {selectedUser.first_name} {selectedUser.last_name}
                              <span style={{ marginLeft:8 }}><RoleBadge role={selectedUser.role} /></span>
                            </div>
                            <div style={{ fontSize:11, color:'#555555', marginTop:3 }}>
                              Toggle individual features on/off. "Grant All" inherits the full module. Changes take effect on next login.
                            </div>
                          </div>
                          <button onClick={savePermissions} disabled={permSaving}
                            style={{ background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:8, color:'white', fontSize:12, padding:'8px 16px', cursor:'pointer', fontWeight:700, opacity: permSaving ? 0.7 : 1, flexShrink:0 }}>
                            {permSaving ? 'Saving…' : '💾 Save Permissions'}
                          </button>
                        </div>

                        {modules.filter(m => m.is_enabled && MODULE_FEATURES[m.slug]).map(m => {
                          const features = MODULE_FEATURES[m.slug]?.features || [];
                          const perms = getPerms(selectedUser.id, m.slug);
                          const allOn = features.every(f => perms[f] !== false);
                          return (
                            <div key={m.slug} style={{ marginBottom:12, background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, overflow:'hidden' }}>
                              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'#0d0d0d', borderBottom:'1px solid #1a1a1a' }}>
                                <div style={{ fontSize:13, fontWeight:700, color:'#e5e5e5' }}>{MODULE_FEATURES[m.slug].label}</div>
                                <button onClick={() => setAllFeatures(selectedUser.id, m.slug, features, !allOn)}
                                  style={{ fontSize:11, background: allOn ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', border:`1px solid ${allOn ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`, color: allOn ? '#fca5a5' : '#4ade80', borderRadius:6, padding:'3px 10px', cursor:'pointer', fontWeight:600 }}>
                                  {allOn ? 'Revoke All' : 'Grant All'}
                                </button>
                              </div>
                              <div style={{ padding:'10px 14px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                                {features.map(f => {
                                  const enabled = perms[f] !== false;
                                  return (
                                    <label key={f} onClick={() => setFeature(selectedUser.id, m.slug, f, !enabled)}
                                      style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color: enabled ? '#e2e8f0' : '#555555', cursor:'pointer', userSelect:'none' }}>
                                      <Toggle on={enabled} onToggle={() => setFeature(selectedUser.id, m.slug, f, !enabled)} />
                                      {f}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}

                        {modules.filter(m => m.is_enabled && MODULE_FEATURES[m.slug]).length === 0 && (
                          <div style={{ color:'#444444', fontSize:13, textAlign:'center', padding:24 }}>
                            Enable modules in the Modules tab first to set feature-level permissions.
                          </div>
                        )}

                        <div style={{ marginTop:16 }}>
                          <button onClick={savePermissions} disabled={permSaving}
                            style={{ width:'100%', padding:12, background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:10, color:'white', fontSize:14, fontWeight:700, cursor: permSaving ? 'wait' : 'pointer', opacity: permSaving ? 0.7 : 1 }}>
                            {permSaving ? 'Saving…' : '💾 Save Permissions for ' + selectedUser.first_name}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── AUDIT LOG TAB ───────────────────────────────────────── */}
                {tab === 'audit' && (
                  <div>
                    <div style={{ fontSize:12, color:'#555555', marginBottom:16 }}>
                      Complete operator activity trail — every action, who performed it, and when.
                    </div>
                    {SEED_AUDIT.map(entry => {
                      const col = entry.sev === 'warning' ? '#f59e0b' : entry.sev === 'critical' ? '#ef4444' : '#3b82f6';
                      const bg  = entry.sev === 'warning' ? 'rgba(245,158,11,0.06)' : entry.sev === 'critical' ? 'rgba(239,68,68,0.06)' : 'rgba(59,130,246,0.06)';
                      return (
                        <div key={entry.id} style={{ display:'flex', gap:12, padding:'10px 12px', background:bg, border:`1px solid ${col}20`, borderRadius:8, marginBottom:6 }}>
                          <div style={{ width:3, borderRadius:2, background:col, flexShrink:0 }}/>
                          <div style={{ flex:1 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                              <div>
                                <span style={{ fontSize:12, fontWeight:700, color:'#e5e5e5' }}>{entry.action.replace(/_/g,' ')}</span>
                                <span style={{ fontSize:11, color:'#555555', marginLeft:8 }}>{entry.module}</span>
                              </div>
                              <span style={{ fontSize:11, color:'#555555', fontFamily:'monospace', flexShrink:0 }}>{entry.ts}</span>
                            </div>
                            <div style={{ fontSize:12, color:'#a0a0a0', marginTop:3 }}>{entry.detail}</div>
                            <div style={{ fontSize:11, color:'#555555', marginTop:2 }}>by {entry.user}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
